/**
 * The whole session as one file to send round afterwards: every shared model
 * and every map that was shown, on one page that opens in any browser and
 * prints (or saves as PDF) cleanly.
 */

import { mapLevel, mapToSvg, modelAncestry, type MapDoc, type SharedModel } from './model';

export interface SessionExport {
  prompt: string;
  /** When the file was made, already formatted for people. */
  date: string;
  people: { name: string; doc: MapDoc | null }[];
  models: SharedModel[];
}

function text(value: string): string {
  return value.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');
}

/** Shared models in reading order: each top-level model, then what goes deeper on it. */
export function modelsInReadingOrder(models: SharedModel[]): SharedModel[] {
  const ordered: SharedModel[] = [];
  const seen = new Set<string>();
  const visit = (model: SharedModel) => {
    if (seen.has(model.id)) return;
    seen.add(model.id);
    ordered.push(model);
    for (const child of models.filter((m) => m.parentModelId === model.id)) visit(child);
  };
  for (const top of models.filter((m) => m.parentModelId === null)) visit(top);
  // Anything whose parent is missing still belongs in the record.
  for (const rest of models) visit(rest);
  return ordered;
}

export function sessionToHtml(session: SessionExport): string {
  const shown = session.people.filter((p) => p.doc !== null && p.doc.shapes.length > 0);
  const notShown = session.people.filter((p) => p.doc === null || p.doc.shapes.length === 0);
  const models = modelsInReadingOrder(session.models).filter((m) => m.doc.shapes.length > 0);

  const figures: string[] = [];
  if (models.length > 0) {
    figures.push('<h2>What we built together</h2>');
    for (const model of models) {
      const path = modelAncestry(session.models, model.id);
      const parent = path.length > 1 ? path[path.length - 2] : null;
      const shape = parent?.doc.shapes.find((s) => s.id === model.parentShapeId)?.label.trim();
      const level = mapLevel(model.level);
      figures.push(
        `<figure><figcaption><strong>${text(model.title)}</strong><span>Level ${level.level} · ${text(level.name)}${
          parent ? ` · a closer look at ${shape ? `“${text(shape)}”` : 'one shape'} in ${text(parent.title)}` : ''
        }</span></figcaption>${mapToSvg(model.doc, '')}</figure>`,
      );
    }
  }
  if (shown.length > 0) {
    figures.push('<h2>How each of us saw it</h2>');
    for (const person of shown) {
      figures.push(`<figure><figcaption><strong>${text(person.name)}</strong></figcaption>${mapToSvg(person.doc!, '')}</figure>`);
    }
  }

  return `<!doctype html>
<html lang="en">
<head>
<meta charset="utf-8">
<meta name="viewport" content="width=device-width, initial-scale=1">
<title>${text(session.prompt)}</title>
<style>
  body { font-family: Helvetica, Arial, sans-serif; color: #1F2937; margin: 0 auto; max-width: 1100px; padding: 32px 24px 64px; }
  h1 { font-size: 26px; margin: 0 0 4px; }
  h2 { font-size: 18px; margin: 40px 0 8px; padding-top: 16px; border-top: 1px solid #D5D9E0; }
  .meta { color: #5B6472; font-size: 14px; margin: 0; }
  figure { margin: 20px 0 32px; break-inside: avoid; }
  figcaption { margin-bottom: 8px; font-size: 15px; }
  figcaption span { color: #5B6472; margin-left: 10px; font-size: 13px; }
  svg { max-width: 100%; height: auto; border: 1px solid #E5E7EB; border-radius: 8px; }
  button { font: inherit; padding: 8px 14px; border: 1px solid #3D4B6F; background: #3D4B6F; color: #fff; border-radius: 6px; cursor: pointer; margin-top: 16px; }
  @media print { button { display: none; } body { padding: 0; } }
</style>
</head>
<body>
<h1>${text(session.prompt)}</h1>
<p class="meta">${text(session.date)} · ${session.people.length} ${session.people.length === 1 ? 'person' : 'people'}: ${text(session.people.map((p) => p.name).join(', '))}</p>
${notShown.length > 0 ? `<p class="meta">Not shown: ${text(notShown.map((p) => p.name).join(', '))}</p>` : ''}
<button onclick="window.print()">Print or save as PDF</button>
${figures.join('\n')}
${figures.length === 0 ? '<p class="meta">Nothing had been shown or built when this was saved.</p>' : ''}
</body>
</html>`;
}
